import React, { useState, useEffect } from 'react';
import { Modal } from '../admin/Modal';
import { Bell, Loader2, RotateCcw } from 'lucide-react';

interface ResolvedWorkflow {
  value: boolean;
  source: 'user' | 'platform';
  label: string;
}

interface NotificationPrefs {
  overrides: Record<string, boolean>;
  resolved: Record<string, ResolvedWorkflow>;
  platformDefaults: Record<string, boolean>;
}

interface NotificationPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
}

export const NotificationPreferencesModal: React.FC<NotificationPreferencesModalProps> = ({
  isOpen, onClose, clientId, clientName
}) => {
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);

  // The /api/email routes are auth-gated; every call must carry the bearer token.
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    if (isOpen && clientId) {
      loadPreferences();
    }
  }, [isOpen, clientId]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/email/user-preferences/${clientId}`, { headers: getAuthHeaders() });
      const result = await response.json();
      if (result.success) {
        setPrefs(result.data);
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (workflow: string, value: boolean | null) => {
    setUpdating(workflow);
    try {
      const response = await fetch(`/api/email/user-preferences/${clientId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ workflow, value })
      });
      const result = await response.json();
      if (result.success) {
        await loadPreferences();
      }
    } catch (error) {
      console.error('Error updating notification preference:', error);
    } finally {
      setUpdating(null);
    }
  };

  const handleToggle = async (workflow: string, currentValue: boolean) => {
    const newValue = !currentValue;
    const platformDefault = prefs?.platformDefaults[workflow] ?? false;
    // If new value matches platform default, clear override (back to inherited)
    await updatePreference(workflow, newValue === platformDefault ? null : newValue);
  };

  const handleReset = async (workflow: string) => {
    // Reset clears the override — back to inheriting platform default
    await updatePreference(workflow, null);
  };

  const handleBulkAction = async (action: 'all-on' | 'all-off' | 'reset') => {
    if (!prefs) return;
    setUpdating('bulk');
    try {
      const workflows = Object.keys(prefs.resolved);
      for (const workflow of workflows) {
        let value: boolean | null;
        if (action === 'reset') {
          value = null;
        } else {
          const desired = action === 'all-on';
          const platformDefault = prefs.platformDefaults[workflow] ?? false;
          // If desired value matches platform default, clear override
          value = desired === platformDefault ? null : desired;
        }
        await fetch(`/api/email/user-preferences/${clientId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workflow, value })
        });
      }
      await loadPreferences();
    } catch (error) {
      console.error('Error updating bulk preferences:', error);
    } finally {
      setUpdating(null);
    }
  };

  const workflows = prefs ? Object.entries(prefs.resolved) : [];
  const hasOverrides = prefs ? Object.keys(prefs.overrides).length > 0 : false;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Notifications — ${clientName}`}
      size="medium"
    >
      <div className="notif-prefs-content">
        <p className="notif-prefs-desc">
          <Bell size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Control which automated emails this client receives. Toggles inherit the platform default unless overridden.
        </p>

        {loading ? (
          <div className="notif-prefs-loading">
            <Loader2 size={20} className="animate-spin" />
            Loading preferences...
          </div>
        ) : workflows.length === 0 ? (
          <p className="notif-prefs-empty">No notification workflows configured.</p>
        ) : (
          <>
            {/* Bulk controls */}
            <div className="notif-bulk-controls">
              <button
                className="notif-bulk-btn"
                onClick={() => handleBulkAction('all-on')}
                disabled={updating === 'bulk'}
              >
                All On
              </button>
              <button
                className="notif-bulk-btn"
                onClick={() => handleBulkAction('all-off')}
                disabled={updating === 'bulk'}
              >
                All Off
              </button>
              {hasOverrides && (
                <button
                  className="notif-bulk-btn reset"
                  onClick={() => handleBulkAction('reset')}
                  disabled={updating === 'bulk'}
                >
                  <RotateCcw size={12} />
                  Reset to Defaults
                </button>
              )}
              {updating === 'bulk' && <Loader2 size={14} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />}
            </div>

            {/* Workflow list */}
            <div className="notif-prefs-list">
              {workflows.map(([key, resolved]) => {
                const isOverridden = resolved.source === 'user';
                const isUpdating = updating === key;
                return (
                  <div key={key} className="notif-pref-item">
                    <div className="notif-pref-info">
                      <span className="notif-pref-label">{resolved.label}</span>
                      <span className="notif-pref-source">
                        {isOverridden ? (
                          <>
                            <span className="notif-override-badge">Override</span>
                            <button
                              className="notif-reset-link"
                              onClick={() => handleReset(key)}
                              disabled={isUpdating}
                            >
                              reset
                            </button>
                          </>
                        ) : (
                          'Inherited from platform'
                        )}
                      </span>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={resolved.value}
                      onClick={() => handleToggle(key, resolved.value)}
                      disabled={isUpdating || updating === 'bulk'}
                      className={`toggle-switch ${resolved.value ? 'on' : 'off'}`}
                    >
                      <span className="toggle-knob" />
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <style>{`
        .notif-prefs-content {
          padding: 4px 0;
        }

        .notif-prefs-desc {
          font-size: 13px;
          color: var(--text-secondary);
          margin: 0 0 16px 0;
          line-height: 1.5;
        }

        .notif-prefs-loading {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-tertiary);
          font-size: 14px;
          padding: 24px 0;
          justify-content: center;
        }

        .notif-prefs-empty {
          color: var(--text-tertiary);
          font-size: 14px;
          text-align: center;
          padding: 24px 0;
        }

        .notif-bulk-controls {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border-tertiary);
        }

        .notif-bulk-btn {
          padding: 4px 12px;
          border-radius: var(--radius-medium);
          font-size: 12px;
          font-weight: 500;
          border: 1px solid var(--border-secondary);
          background: var(--bg-secondary);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .notif-bulk-btn:hover:not(:disabled) {
          background: var(--border-secondary);
          color: var(--text-primary);
        }

        .notif-bulk-btn:disabled {
          opacity: 0.5;
          cursor: wait;
        }

        .notif-bulk-btn.reset {
          margin-left: auto;
        }

        .notif-prefs-list {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .notif-pref-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px;
          border-radius: var(--radius-medium);
          transition: background 0.15s ease;
        }

        .notif-pref-item:hover {
          background: var(--bg-secondary);
        }

        .notif-pref-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .notif-pref-label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary);
        }

        .notif-pref-source {
          font-size: 12px;
          color: var(--text-tertiary);
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .notif-override-badge {
          font-size: 11px;
          font-weight: 600;
          color: var(--accent-blue);
          background: rgba(59, 130, 246, 0.1);
          padding: 1px 6px;
          border-radius: 4px;
        }

        .notif-reset-link {
          background: none;
          border: none;
          color: var(--text-tertiary);
          font-size: 11px;
          cursor: pointer;
          padding: 0;
          text-decoration: underline;
          transition: color 0.15s ease;
        }

        .notif-reset-link:hover:not(:disabled) {
          color: var(--text-secondary);
        }

        .notif-reset-link:disabled {
          opacity: 0.5;
          cursor: wait;
        }

        .toggle-switch {
          position: relative;
          width: 48px;
          height: 26px;
          border-radius: 13px;
          border: none;
          cursor: pointer;
          transition: background-color 0.2s ease;
          padding: 0;
          flex-shrink: 0;
        }

        .toggle-switch.off {
          background-color: var(--border-secondary);
        }

        .toggle-switch.on {
          background-color: var(--accent-green);
        }

        .toggle-switch:disabled {
          opacity: 0.5;
          cursor: wait;
        }

        .toggle-knob {
          position: absolute;
          top: 3px;
          left: 3px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: white;
          transition: transform 0.2s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }

        .toggle-switch.on .toggle-knob {
          transform: translateX(22px);
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Modal>
  );
};
